import { z } from 'zod'
import { asEnumType, asObjectType } from '@gqloom/zod'
import { numericSchema } from '@hapana/shared/backend/spanner'
import { SessionSchema } from '../site-sessions/site-session-schemas'

// Enums with GraphQL names
export const BookingStatusEnum = z
  .enum([
    'PENDING',
    'CONFIRMED',
    'DECLINED',
    'CANCELLED',
    'LATE_CANCELLED',
    'ATTENDED',
    'NO_SHOW',
    'INVITED',
    'EXPIRED',
    'REJECTED',
  ])
  .register(asEnumType, {
    name: 'BookingStatus',
  })

export type BookingStatus = z.infer<typeof BookingStatusEnum>

// Valid statuses for attendance transitions (used by updateBookingAttendance mutation)
// CONFIRMED = reset attendance, ATTENDED = checked in, NO_SHOW = didn't show, LATE_CANCELLED = late cancel from check-in
export const AttendanceStatusEnum = z
  .enum(['CONFIRMED', 'ATTENDED', 'NO_SHOW', 'LATE_CANCELLED'])
  .register(asEnumType, {
    name: 'AttendanceStatus',
  })

export type AttendanceStatus = z.infer<typeof AttendanceStatusEnum>

export const ACTIVE_BOOKING_STATUSES: BookingStatus[] = ['CONFIRMED', 'PENDING']

export const CANCELLED_BOOKING_STATUSES: BookingStatus[] = ['CANCELLED', 'LATE_CANCELLED']

export const VISIBLE_BOOKING_STATUSES: BookingStatus[] = [
  'CONFIRMED',
  'PENDING',
  'ATTENDED',
  'LATE_CANCELLED',
  'NO_SHOW',
]

export const OVERRIDABLE_BOOKING_STATUSES = ['LATE_CANCELLED', 'NO_SHOW', 'ATTENDED'] as const

export const BookingSourceEnum = z
  .enum([
    'CREDIT', // booking paid with session credits
    'FREE', // complimentary / comp booking
    'DROP_IN', // paid at the door
    'BILLING', // booking created via billing / membership auto-renewal
    'ADMIN', // booking created by admin staff override
    'AGGREGATOR', // booking created by an aggregator provider (ClassPass, FitMate, etc.)
    'IMPORT', // booking created via CSV import
  ])
  .register(asEnumType, {
    name: 'BookingSource',
  })

export type BookingSourceValue = z.infer<typeof BookingSourceEnum>

// Payment status for bookings - tracks whether payment has been received
// Note: Named 'BookingPaymentStatus' to avoid GraphQL federation conflict with
// Payment.status (PaymentStatus) which has different values for transaction states
export const PaymentStatusEnum = z.enum(['paid', 'pending']).register(asEnumType, {
  name: 'BookingPaymentStatus',
})

export type PaymentStatus = z.infer<typeof PaymentStatusEnum>

export const SourceEnum = z
  .enum(['WEB', 'APP', 'STAFF', 'KIOSK', 'AGGREGATOR'])
  .register(asEnumType, {
    name: 'Source',
  })

/**
 * Refund method options for paid DROP_IN booking cancellations.
 * These are UI-friendly values that get mapped to internal RefundMethod values:
 * - ACCOUNT_CREDIT -> RefundMethod.AccountCredit
 * - ORIGINAL_PAYMENT_METHOD -> RefundMethod.Gateway
 */
export const DropInRefundMethodEnum = z
  .enum(['ACCOUNT_CREDIT', 'ORIGINAL_PAYMENT_METHOD'])
  .register(asEnumType, {
    name: 'DropInRefundMethod',
    description: 'Refund method for paid drop-in booking cancellations',
  })

export type DropInRefundMethod = z.infer<typeof DropInRefundMethodEnum>

export const BookingFeeTypeEnum = z.enum(['NO_SHOW', 'LATE_CANCEL']).register(asEnumType, {
  name: 'BookingFeeType',
  description: 'Type of booking fee (no-show or late cancel)',
})

export type BookingFeeType = z.infer<typeof BookingFeeTypeEnum>

// Main Booking Schema
export const BookingSchema = z
  .object({
    bookingId: z.uuid(),
    clientId: z.uuid(),
    siteId: z.uuid(),
    sessionId: z.uuid(),
    status: BookingStatusEnum,
    bookingSource: BookingSourceEnum.nullable(),
    paymentStatus: PaymentStatusEnum.nullable(), // null = free, 'paid' = credits consumed or drop-in paid, 'pending' = awaiting payment
    totalCreditsRequired: z.number().int(),
    dropInInvoiceId: z.uuid().nullable(),
    dropInAmount: numericSchema.nullable(),
    lateCancelFeeInvoiceId: z.uuid().nullable(),
    lateCancelCreditsKept: z.number().int().nullable(),
    noShowFeeInvoiceId: z.uuid().nullable(),
    invitedByStaffUserId: z.uuid().nullable(),
    invitationSentAt: z.date().nullable(),
    invitationAcceptedAt: z.date().nullable(),
    confirmedAt: z.date().nullable(),
    cancelledAt: z.date().nullable(),
    cancelledBy: z.uuid().nullable(),
    source: SourceEnum,
    notes: z.string().nullable(),
    spotId: z.number().int().nullable(),
    externalReferenceId: z.string().max(255).nullable(),
    externalUserId: z.string().max(255).nullable(),
    aggregatorProviderId: z.string().max(36).nullable().optional(),
    // When clientId !== bookedByClientId, this booking was made on behalf of
    // the target client by another (buddy) client. Null = self-booked or
    // pre-existing row before the column was added (CORE-1460).
    bookedByClientId: z.uuid().nullable().optional(),
    // Gates cancel-time financial reversals in BookingsService.cancel.
    isImported: z.boolean(),
    createdAt: z.date(),
    updatedAt: z.date(),
    // Client name - computed from JOIN to avoid N+1 queries
    clientName: z.string().nullable().optional(),
    // Attendance counts - optionally populated when includeAttendanceCounts is true
    siteAttendanceCount: z.number().int().nullable().optional(),
    totalAttendanceCount: z.number().int().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'Booking',
    description: 'A booking for a session',
  })

export type Booking = z.infer<typeof BookingSchema>

// Spanner row type - used for typing raw database records before parsing
// Note: Spanner NUMERIC fields are returned as Big.js instances
export type BookingRow = {
  bookingId: string
  clientId: string
  siteId: string
  sessionId: string
  status: string
  bookingSource: string | null
  paymentStatus: string | null
  totalCreditsRequired: number
  dropInInvoiceId: string | null
  dropInAmount: Big | null // Spanner NUMERIC returns Big.js instance
  lateCancelFeeInvoiceId: string | null
  lateCancelCreditsKept: number | null
  noShowFeeInvoiceId: string | null
  invitedByStaffUserId: string | null
  invitationSentAt: Date | null
  invitationAcceptedAt: Date | null
  confirmedAt: Date | null
  cancelledAt: Date | null
  cancelledBy: string | null
  source: string
  notes: string | null
  spotId: number | null
  externalReferenceId: string | null
  externalUserId: string | null
  aggregatorProviderId: string | null
  bookedByClientId: string | null
  isImported: boolean
  createdAt: Date
  updatedAt: Date
  // Computed field from JOIN
  clientName?: string | null
}

// Create Booking Input Schema
export const BookingCreateSchema = z
  .object({
    clientId: z.string().uuid(),
    // Allow both UUID and virtual session ID format (virtual_<uuid>_YYYY-MM-DD)
    sessionId: z
      .string()
      .refine(
        (id) =>
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) ||
          /^virtual_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_\d{4}-\d{2}-\d{2}$/i.test(
            id
          ),
        'sessionId must be a valid UUID or virtual session ID'
      ),
    status: BookingStatusEnum.optional(),
    bookingSource: BookingSourceEnum,
    paymentStatus: PaymentStatusEnum.nullable().optional(),
    totalCreditsRequired: z.number().int().optional(),
    dropInAmount: numericSchema.nullable().optional(),
    invitedByStaffUserId: z.string().uuid().nullable().optional(),
    source: SourceEnum.optional(),
    notes: z.string().nullable().optional(),
    spotId: z.number().int().nullable().optional(),
    externalReferenceId: z.string().max(255).nullable().optional(),
    externalUserId: z.string().max(255).nullable().optional(),
    aggregatorProviderId: z.string().max(36).nullable().optional(),
    serviceId: z.string().uuid().nullable().optional(),
    creditOwnerClientId: z.string().uuid().nullable().optional(),
    bookedByClientId: z.string().uuid().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'BookingCreateInput',
    description: 'Input for creating a booking',
  })

export type BookingCreateInput = z.infer<typeof BookingCreateSchema>

/**
 * Input shape for the per-row write inside `BookingsService.bulkCreateForImport`.
 *
 * Mirrors `BookingCreateSchema` (the schema the per-row `create` enforces)
 * with caller-attached `bookingId` + `siteId` for retry idempotency. The
 * transformer builds full row data, including any pre-resolved `spotId`,
 * payment status, and credits required.
 *
 * Intentional deviations from pure-mirror:
 * - Virtual session materialization is NOT done — transformers pass real
 *   `sessionId` UUIDs because the Session rows are written in the same
 *   batch (FK ordering preserved by the transformer).
 * - `validateBookingCapacity` is NOT called — historical imports must
 *   accept capacity that exceeds the session size.
 * - `createBookingWithPayment` payment flow (credit deduction, invoice
 *   generation, ledger writes) is NOT invoked — the import path does not
 *   process payments at execute-stage.
 * - `serviceIdsForSnapshot` balance-snapshot refresh, confirmation
 *   emails, audit publish, and IntegrationEventService notifications are
 *   NOT called — historical import path side effects.
 */
export const BookingBulkCreateForImportSchema = BookingCreateSchema.safeExtend({
  bookingId: z.uuid(),
  siteId: z.uuid(),
  isImported: z.boolean().optional(),
})

export type BookingBulkCreateForImportSchema = z.infer<typeof BookingBulkCreateForImportSchema>

// Update Booking Input Schema
export const BookingUpdateSchema = z
  .object({
    status: BookingStatusEnum.optional(),
    bookingSource: BookingSourceEnum.nullable().optional(),
    paymentStatus: PaymentStatusEnum.nullable().optional(),
    totalCreditsRequired: z.number().int().optional(),
    dropInAmount: numericSchema.nullable().optional(),
    dropInInvoiceId: z.string().uuid().nullable().optional(),
    lateCancelCreditsKept: z.number().int().nullable().optional(),
    lateCancelFeeInvoiceId: z.string().uuid().nullable().optional(),
    noShowFeeInvoiceId: z.string().uuid().nullable().optional(),
    notes: z.string().nullable().optional(),
    spotId: z.number().int().nullable().optional(),
    cancelledAt: z.date().nullable().optional(),
    cancelledBy: z.string().uuid().nullable().optional(),
    invitationAcceptedAt: z.date().nullable().optional(),
    confirmedAt: z.date().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'BookingUpdateInput',
    description: 'Input for updating a booking',
  })

export type BookingUpdateInput = z.infer<typeof BookingUpdateSchema>

// Pagination Args Schema
export const BookingPaginationArgsSchema = z
  .object({
    siteId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    clientId: z.string().uuid().optional(),
    status: BookingStatusEnum.optional(),
    after: z.string().optional(),
    before: z.string().optional(),
    pageSize: z.number().int().min(1).max(100).default(50),
  })
  .register(asObjectType, {
    name: 'BookingPaginationArgs',
    description: 'Arguments for booking pagination',
  })

export type BookingPaginationArgs = z.infer<typeof BookingPaginationArgsSchema>

// Pagination Result Schema
export const BookingPaginationResultSchema = z
  .object({
    results: z.array(
      z.object({
        row: BookingSchema,
        cursor: z.string(),
      })
    ),
    pageInfo: z.object({
      hasNextPage: z.boolean(),
      startCursor: z.string().nullable(),
      endCursor: z.string().nullable(),
      totalRecords: z.number().int(),
    }),
  })
  .register(asObjectType, {
    name: 'BookingPaginationResult',
    description: 'Result of booking pagination',
  })

export type BookingPaginationResult = z.infer<typeof BookingPaginationResultSchema>

// Booking Activity Log Types
export const BOOKING_ACTIVITY_TYPE = {
  BOOKING_CREATED: 'booking_created',
  STATUS_CHANGED: 'status_changed',
  ATTENDANCE_UPDATED: 'attendance_updated',
  SPOT_CHANGED: 'spot_changed',
  BOOKING_CANCELLED: 'booking_cancelled',
  BOOKING_LATE_CANCELLED: 'booking_late_cancelled',
  BOOKING_REINSTATED: 'booking_reinstated',
  SESSION_CANCELLED: 'session_cancelled',
  INVITATION_SENT: 'invitation_sent',
  INVITATION_ACCEPTED: 'invitation_accepted',
} as const

export const BookingActivityTypeEnum = z
  .enum([
    BOOKING_ACTIVITY_TYPE.BOOKING_CREATED,
    BOOKING_ACTIVITY_TYPE.STATUS_CHANGED,
    BOOKING_ACTIVITY_TYPE.ATTENDANCE_UPDATED,
    BOOKING_ACTIVITY_TYPE.SPOT_CHANGED,
    BOOKING_ACTIVITY_TYPE.BOOKING_CANCELLED,
    BOOKING_ACTIVITY_TYPE.BOOKING_LATE_CANCELLED,
    BOOKING_ACTIVITY_TYPE.BOOKING_REINSTATED,
    BOOKING_ACTIVITY_TYPE.SESSION_CANCELLED,
    BOOKING_ACTIVITY_TYPE.INVITATION_SENT,
    BOOKING_ACTIVITY_TYPE.INVITATION_ACCEPTED,
  ])
  .register(asEnumType, {
    name: 'BookingActivityType',
  })

export type BookingActivityType = z.infer<typeof BookingActivityTypeEnum>

// Created By Type - who created the activity log entry
export const CREATED_BY_TYPE = {
  STAFF: 'STAFF',
  CLIENT: 'CLIENT',
  SYSTEM: 'SYSTEM',
  AGGREGATOR: 'AGGREGATOR',
} as const

export const CreatedByTypeEnum = z
  .enum([
    CREATED_BY_TYPE.STAFF,
    CREATED_BY_TYPE.CLIENT,
    CREATED_BY_TYPE.SYSTEM,
    CREATED_BY_TYPE.AGGREGATOR,
  ])
  .register(asEnumType, {
    name: 'CreatedByType',
    description: 'Type of actor who created the activity: STAFF, CLIENT, or SYSTEM',
  })

export type CreatedByType = z.infer<typeof CreatedByTypeEnum>

// Booking Activity Log Schema
export const BookingActivityLogSchema = z
  .object({
    bookingActivityLogId: z.string().uuid(),
    bookingId: z.string().uuid(),
    siteId: z.string().uuid(),
    clientId: z.string().uuid(),
    activityType: BookingActivityTypeEnum,
    value: z.string().nullable(),
    createdAt: z.coerce.date(),
    createdBy: z.string().uuid().nullable(),
    createdByType: CreatedByTypeEnum.nullable().optional(),
    createdByName: z.string().nullable().optional(),
    createdByFirstName: z.string().nullable().optional(),
    createdByLastName: z.string().nullable().optional(),
    clientFirstName: z.string().nullable().optional(),
    clientLastName: z.string().nullable().optional(),
    sessionName: z.string().nullable().optional(),
    sessionStartTime: z.coerce.date().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'BookingActivityLog',
    description: 'Activity log entry for booking changes',
  })

export type BookingActivityLog = z.infer<typeof BookingActivityLogSchema>

// Spanner row type for BookingActivityLog
export type BookingActivityLogRow = {
  bookingActivityLogId: string
  bookingId: string
  siteId: string
  clientId: string
  activityType: string
  value: string | null
  createdAt: Date
  createdBy: string | null
  createdByType: string | null
  createdByName?: string | null
  createdByFirstName?: string | null
  createdByLastName?: string | null
  clientFirstName?: string | null
  clientLastName?: string | null
  sessionName?: string | null
  sessionStartTime?: Date | null
}

// Create Booking Activity Log Input Schema
export const BookingActivityLogCreateSchema = z
  .object({
    bookingId: z.string().uuid(),
    siteId: z.string().uuid(),
    clientId: z.string().uuid(),
    activityType: BookingActivityTypeEnum,
    value: z.string().nullable().optional(),
    createdBy: z.string().uuid().nullable().optional(),
    createdByType: CreatedByTypeEnum.nullable().optional(),
  })
  .register(asObjectType, {
    name: 'BookingActivityLogCreateInput',
    description: 'Input for creating a booking activity log entry',
  })

export type BookingActivityLogCreateInput = z.infer<typeof BookingActivityLogCreateSchema>

/**
 * Input shape for the per-row write inside
 * `BookingActivityLogService.bulkCreateForImport`.
 *
 * Mirrors `BookingActivityLogCreateSchema` (the schema enforced by the per-row
 * `create`) with `bookingActivityLogId` attached: the caller owns the
 * deterministic ID so retries are idempotent. Unlike the per-row `create`,
 * the transformer pre-fills `createdBy` and `createdByType` per row (typically
 * `null` + `CREATED_BY_TYPE.SYSTEM` for imports) rather than the wrapper
 * deriving them from `useContext('user')`. The repo bulk method assigns
 * `createdAt = new Date()` to match the per-row write contract.
 */
export const BookingActivityLogBulkCreateForImportSchema =
  BookingActivityLogCreateSchema.safeExtend({
    bookingActivityLogId: z.uuid(),
  })

export type BookingActivityLogBulkCreateForImportSchema = z.infer<
  typeof BookingActivityLogBulkCreateForImportSchema
>

// Client Attendance Counts Schema
export const ClientAttendanceCountsSchema = z
  .object({
    clientId: z.string().uuid(),
    siteAttendanceCount: z.number().int(),
    totalAttendanceCount: z.number().int(),
  })
  .register(asObjectType, {
    name: 'ClientAttendanceCounts',
    description: 'Attendance counts for a client at site and total levels',
  })

export type ClientAttendanceCounts = z.infer<typeof ClientAttendanceCountsSchema>

// Client Attendance Stats Schema (for client overview page)
export const MonthlyAttendanceSchema = z
  .object({
    month: z.string(),
    count: z.number().int(),
  })
  .register(asObjectType, {
    name: 'MonthlyAttendance',
    description: 'Monthly attendance count for a specific month',
  })

export type MonthlyAttendance = z.infer<typeof MonthlyAttendanceSchema>

export const ClientAttendanceStatsSchema = z
  .object({
    totalAttendedCount: z.number().int(),
    attendedLast30Days: z.number().int(),
    noShowCount: z.number().int(),
    upcomingCount: z.number().int(),
    lastAttendanceDate: z.date().nullable(),
    siteJoinDate: z.date().nullable(),
    monthlyAttendance: z.array(MonthlyAttendanceSchema),
  })
  .register(asObjectType, {
    name: 'ClientAttendanceStats',
    description:
      'Attendance statistics for a client at a site, including aggregate counts and monthly breakdown',
  })

export type ClientAttendanceStats = z.infer<typeof ClientAttendanceStatsSchema>

// ============================================================================
// Private Booking (request booking) input schema
// ============================================================================

/** Input for creating a private session from template and booking atomically */
export const PrivateBookingCreateSchema = z
  .object({
    sessionTemplateId: z.string().min(1, 'Session template ID is required'),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
    startTime: z.coerce.date(),
    clientId: z.string().uuid(),
    staffUserIds: z.array(z.string().uuid()).optional(),
    serviceId: z.string().uuid().nullable().optional(),
    notes: z.string().nullable().optional(),
    bookingSource: BookingSourceEnum.optional(),
  })
  .register(asObjectType, {
    name: 'PrivateBookingCreateInput',
    description:
      'Input for creating a private session from template and booking the client atomically',
  })

export type PrivateBookingCreateInput = z.infer<typeof PrivateBookingCreateSchema>

// ============================================================================
// Service-level schemas (internal types for booking operations)
// ============================================================================

/** Options for booking cancellation */
export const BookingCancelOptionsSchema = z.object({
  /** Required for paid DROP_IN bookings - specifies where to send the refund */
  refundMethod: DropInRefundMethodEnum.optional(),
  feeRefundMethod: DropInRefundMethodEnum.optional(),
  /**
   * When true, bypass late-cancel logic and always treat as early cancellation.
   * Used by service suspension/cancellation flows where the cancellation reason
   * is the service state change, not a client-initiated late cancel.
   */
  skipLateCancelCheck: z.boolean().optional(),
})
export type BookingCancelOptions = z.infer<typeof BookingCancelOptionsSchema>

/** Payment strategy result for booking creation */
export const PaymentStrategySchema = z.object({
  status: BookingStatusEnum,
  bookingSource: BookingSourceEnum,
  paymentStatus: PaymentStatusEnum.nullable(),
  dropInAmount: numericSchema.nullable(),
  requiresCredits: z.boolean(),
  creditsRequired: z.number().int(),
})
export type PaymentStrategy = z.infer<typeof PaymentStrategySchema>

/** Input for core booking creation with payment */
export const CreateBookingWithPaymentInputSchema = z.object({
  siteId: z.string().uuid(),
  sessionId: z.string().uuid(),
  clientId: z.string().uuid(),
  // When set, credits are consumed from this client's services rather than
  // the booking client's. Used for buddy bookings where the booker pays from
  // their own package.
  creditOwnerClientId: z.string().uuid().optional(),
  // The client who initiated the booking — distinct from the booking's owner
  // when booking on behalf of an accepted buddy. Stored on Booking.bookedByClientId
  // and used as the invoice payer for drop-in flows.
  bookedByClientId: z.string().uuid().optional(),
  session: SessionSchema,
  spotId: z.number().int().nullable(),
  notes: z.string().nullable().optional(),
  invitedByStaffUserId: z.string().uuid().nullable().optional(),
  // Preferred service for credit consumption (from package selection)
  serviceId: z.string().uuid().nullable().optional(),
  // Waitlist conversion fields
  waitlistEntryId: z.string().uuid().optional(),
  prePaidCreditLedgerIds: z.array(z.string().uuid()).optional(),
  // Optional booking source override (e.g. 'DROP_IN' for book-now-pay-later)
  bookingSourceOverride: BookingSourceEnum.nullable().optional(),
  // Where the booking originated (APP, WEB, STAFF, KIOSK, AGGREGATOR)
  source: SourceEnum.optional(),
})
export type CreateBookingWithPaymentInput = z.infer<typeof CreateBookingWithPaymentInputSchema>

/** Result from core booking creation */
export const CreateBookingWithPaymentResultSchema = z.object({
  booking: BookingSchema,
  paymentStrategy: PaymentStrategySchema,
  isFirstBooking: z.boolean(),
  serviceIdsForSnapshot: z.array(z.uuid()),
})
export type CreateBookingWithPaymentResult = z.infer<typeof CreateBookingWithPaymentResultSchema>

/**
 * Partial booking data returned for check-in window queries
 * Contains only the fields needed for check-in processing (multi-session discovery)
 */
export type BookingForCheckIn = Pick<
  Booking,
  | 'bookingId'
  | 'clientId'
  | 'siteId'
  | 'sessionId'
  | 'status'
  | 'bookingSource'
  | 'totalCreditsRequired'
  | 'createdAt'
  | 'updatedAt'
>
